import {
    Component,
    Input,
    TemplateRef,
    ViewChild,
    inject,
} from '@angular/core';
import {
    CalendarCommonModule,
    CalendarEvent,
    CalendarMonthModule,
    CalendarView,
} from 'angular-calendar';
import {
    NgbModal,
    NgbModalModule,
    NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { CommonModule, registerLocaleData } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ApplicationService } from 'app/services/application.service';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { CalendarDetailsComponent } from '../calendar-details/calendar-details.component';
import { CalendarEventsComponent } from '../calendar-events/calendar-events.component';
import { TooltipComponent } from '../tooltip/tooltip.component';
import localeMs from '@angular/common/locales/ms';
import { formatTime } from 'app/shared/utils';
import { LoaderComponent } from '../loader/loader.component';

registerLocaleData(localeMs);

const eventCategorieColors = {
    financialAdvisory: '#FBFB76',
    advisoryWorkshops: '#FF607A',
    newMarkets: '#E1A0FF',
    networking: '#FF8132',
};

@Component({
    selector: 'app-calendar-view',
    templateUrl: './calendar-view.component.html',
    standalone: true,
    imports: [
        CalendarMonthModule,
        CommonModule,
        RouterModule,
        TranslateModule,
        CalendarCommonModule,
        MatIconModule,
        NgbModalModule,
        NgbTooltipModule,
        CalendarEventsComponent,
        CalendarDetailsComponent,
        TooltipComponent,
        LoaderComponent
    ],
})
export class CalendarViewComponent {
    @ViewChild('modalContent', { static: true }) modalContent: TemplateRef<any>;

    heading = 'LNDP_CALENDAR.PROGRAM_CALENDAR';

    eventCategories = [
        {
            color: `tw-bg-[#EF0002]`,
            label: 'LNDP_CALENDAR.ADVISORY_WORKSHOP',
        },
        {
            color: `tw-bg-[#FEC607]`,
            label: 'LNDP_CALENDAR.ACCESS_NEW_MARKETS',
        },
        {
            color: `tw-bg-[#009242]`,
            label: 'LNDP_CALENDAR.NETWORKING_SESSION',
        },
    ];
    subCategories = [
        {
            label: 'LNDP_CALENDAR.BUSINESS_PLAN',
        },
        {
            label: 'LNDP_CALENDAR.FINANCIAL_LITERACY',
        },
        {
            label: 'LNDP_CALENDAR.MARKETING_SALES',
        },
        {
            label: 'LNDP_CALENDAR.HALAL',
        },
        {
            label: 'COMMON.ESG',
        },
        {
            label: 'LNDP_CALENDAR.BUSINESS_EXPANSION',
        },
    ];

    view: CalendarView = CalendarView.Month;

    CalendarView = CalendarView;

    viewDate: Date = new Date();

    modalData: {
        action: string;
        event: CalendarEvent[];
    };

    refresh = new Subject<void>();

    @Input() calendarRes;
    events: CalendarEvent[] = [];
    apiResponse: boolean = false;

    selectedLanguage: any;
    tagsAcronym = [
        {
            tags: 'LNDP_CALENDAR.AW',
            class: '#EF0002',
        },
        {
            tags: 'LNDP_CALENDAR.ANM',
            class: '#FEC607',
        },
        {
            tags: 'LNDP_CALENDAR.NS',
            class: '#009242',
        },
    ];

    private modalService = inject(NgbModal);

    initialProductsList = [];
    filteredProducts = [];
    checkboxStates: { [key: string]: boolean } = {};

    constructor(private applicationService: ApplicationService) { }

    ngOnInit() {
        this.selectedLanguage = localStorage.getItem('language');
        //get events by current month and year
        const date = new Date();
        let month = date.getMonth() + 1;
        let currentMonth = month;
        let currentYear = date.getFullYear();
        this.getCalendarEventsRes(currentMonth, currentYear);
    }

    getCalendarEventsRes(month, year) {
        this.applicationService.getCalendarDetails(month, year).subscribe({
            next: (res) => {
                this.apiResponse = true;                
                this.calendarRes = res;
                //get events by current month and year
                this.getCalendarEvents();
                
                /* For Scroll Section after getting list of Calendar response*/
                this.findYPosition('app-footer');
                this.findYPosition('app-calendar-view');
                this.findYPosition('app-calendar-view-mobile');
            },
            error: (err) => {
                this.apiResponse = true;
             },
        });
    }

    findYPosition(elementName: any) {
        const note = document.getElementsByTagName(elementName)[0];
        const details = note.getBoundingClientRect();
        const initialYPosition = (details.y).toString();
        const nameSet = 'YPosition-' + elementName;
        localStorage.setItem(nameSet, initialYPosition);
    }

    getCalendarEvents() {
        let calendarRes = this.calendarRes;
        //format data for events list and calendar details popup
        this.events = calendarRes
            .map((element) => {
                const inputDate: any = new Date(element.date).setHours(
                    0,
                    0,
                    0,
                    0
                );
                const currentDate: any = new Date().setHours(0, 0, 0, 0);
                const getCurrentTime = new Date().toLocaleTimeString();

                let isPastDate: boolean = false;

                if (inputDate < currentDate) {
                    element['class'] = '#D5D4D4';
                    isPastDate = true;
                } else if (inputDate == currentDate) {
                    if (element.startTime < getCurrentTime) {
                        isPastDate = true;
                    }
                }

                if (element.category === 'Advisory Workshops') {
                    element.tags = this.tagsAcronym[0].tags;
                    if (!isPastDate) {
                        element['class'] = this.tagsAcronym[0].class;
                    }
                } else if (element.category === 'Access New Markets') {
                    element.tags = this.tagsAcronym[1].tags;
                    if (!isPastDate) {
                        element['class'] = this.tagsAcronym[1].class;
                    }
                } else if (element.category === 'Networking Sessions') {
                    element.tags = this.tagsAcronym[2].tags;
                    if (!isPastDate) {
                        element['class'] = this.tagsAcronym[2].class;
                    }
                }

                let timeDiff = this.getTimeDiff(
                    element.endTime,
                    element.startTime
                );

                const isEnableEvent = element.enabled == 'yes';

                let isRegisterEventDisable = false;
                if (isPastDate) {
                    isRegisterEventDisable = true;
                } else if (
                    !isEnableEvent ||
                    element.isRegistered == 'Registered to Event'
                ) {
                    isRegisterEventDisable = true;
                }

                return {
                    id: element.id,
                    start: new Date(element.date),
                    date: this.formatDate(element.date),
                    title: element.title,
                    tags: element.tags,
                    meta: {
                        mode: element.eventType,
                        class: `tw-bg-[${element['class']}]`,
                    },
                    category: element.category,
                    subcategory: element.subcategory,
                    venue: element.venue,
                    eventDescription: element.eventDescription,
                    link: element['link'],
                    startTime: formatTime(element.startTime),
                    endTime: formatTime(element.endTime),
                    isRegistered: element.isRegistered,
                    tophat: element.tophat,
                    timeDiff: timeDiff,
                    pastDate: isPastDate,
                    enabled: element.enabled,
                    isRegisterEventDisable,
                };
            })
            .sort(this.sortByDate);
        const initialArray = this.events;
        this.initialProductsList = this.nestedCopy(initialArray);
    }

    sortByDate = (a, b) => {
        let date1: any = new Date(a.start);
        let date2: any = new Date(b.start);
        return date1 - date2;
    };

    closeOpenMonthViewDay() {
        const date = this.viewDate;
        let month = date.getMonth() + 1;
        let selectedMonth = month;
        let selectedYear = date.getFullYear();
        this.getCalendarEventsRes(selectedMonth, selectedYear);
    }

    onClickEvent(date, id) {
        let selectedDate = new Date(date);
        let openCalendarDetails = false;
        let eventData: any = this.events.filter((element: any) => {
            const currentDate: any = new Date(element?.date).setHours(
                0,
                0,
                0,
                0
            );
            const userSelectedDate: any = selectedDate.setHours(0, 0, 0, 0);
            if (userSelectedDate === currentDate && id === element.id) {
                openCalendarDetails = true;
                return userSelectedDate === currentDate;
            }
        });
        if (openCalendarDetails) {
            this.modalData = {
                event: eventData[0],
                action: '',
            };
            const modalRef = this.modalService.open(CalendarDetailsComponent, {
                size: 'md',
                backdrop: true,
                centered: true,
                windowClass: 'calendar-view-details',
            });
            modalRef.componentInstance.data = this.modalData;
        }
    }

    convertToTimeFormat(dateTimeString: string) {
        return dateTimeString.split(' ')[1];
    }

    getTimeDiff(t2, t1) {
        let getT2 = t2.split(':');
        let getT1 = t1.split(':');
        let t2Hrs = getT2[0];
        let t2Mins = getT2[1];
        let t1Hrs = getT1[0];
        let t1Mins = getT1[1];

        let hours;
        let mins;

        if (t1Hrs < t2Hrs) {
            hours = t2Hrs - t1Hrs;
        } else if (t1Hrs > t2Hrs) {
            hours = t1Hrs - t2Hrs;
        } else if (t1Hrs === t2Hrs) {
            hours = t2Hrs - t1Hrs;
        }

        if (t1Mins < t2Mins) {
            mins = t2Mins - t1Mins;
        } else if (t1Mins > t2Mins || t1Mins === t2Mins) {
            mins = t1Mins - t2Mins;
        }

        let hrsFormat;
        let hrsString;
        if (hours > 1) {
            hrsString = 'hrs ';
            hrsFormat = hours + hrsString;
        } else if (hours === 1) {
            hrsString = 'hr ';
            hrsFormat = hours + hrsString;
        } else if (hours === 0) {
            hrsString = '';
            hrsFormat = '';
        }

        let minsFormat;
        let minsString;
        if (mins > 9) {
            minsString = 'mins ';
            minsFormat = mins + minsString;
        } else if (mins > 0 && mins < 10) {
            minsString = 'min ';
            minsFormat = mins + minsString;
        } else if (mins === 0) {
            minsString = '';
            minsFormat = '';
        }
        return hrsFormat + minsFormat;
    }

    changeMonthLanguage(e: any) {
        this.selectedLanguage = e;
        this.closeOpenMonthViewDay();
    }

    formatDate(inputTime: string) {
        let dateObj = new Date(inputTime);
        let langDate = '';
        if (this.selectedLanguage == 'bm') {
            langDate = 'ms-MY';
        } else {
            langDate = 'en-US';
        }
        let month = dateObj.toLocaleString(langDate, { month: 'short' });
        let day = dateObj.getDate();
        let year = dateObj.getFullYear();
        let formattedDate = day + ' ' + month;
        return formattedDate;
    }

    onSubmit(link) {
        window.open(link, '_blank');
    }

    addIfNotExists(value: any, array: any[]): void {
        // Check if the value is not already present in the array
        if (array.indexOf(value) === -1) {
            // If not present, add the value to the array
            array.push(value);
        }
    }

    removeProviders(prodCat) {
        let removeIndex = this.filteredProducts.findIndex(
            (item) => item === prodCat
        );
        if (removeIndex !== -1) {
            this.filteredProducts.splice(removeIndex, 1);
        }
    }

    onFilterProduct(event, prodCategory) {
        let prodCat = prodCategory;
        this.checkboxStates[prodCat] = event.target.checked;
        let selectedProdCat = prodCat;

        if (prodCat === 'Advisory Workshops') {
            this.checkboxStates['Business Plan'] = event.target.checked;
            this.checkboxStates['Financial Literacy'] = event.target.checked;
            this.checkboxStates['Marketing and Sales'] = event.target.checked;
            this.checkboxStates['Halal'] = event.target.checked;
            this.checkboxStates['ESG'] = event.target.checked;
            this.checkboxStates['Business Expansion'] = event.target.checked;
            if (event.target.checked) {
                this.addIfNotExists(
                    'Advisory Workshops',
                    this.filteredProducts
                );
                this.addIfNotExists('Business Plan', this.filteredProducts);
                this.addIfNotExists(
                    'Financial Literacy',
                    this.filteredProducts
                );
                this.addIfNotExists(
                    'Marketing and Sales',
                    this.filteredProducts
                );
                this.addIfNotExists('Halal', this.filteredProducts);
                this.addIfNotExists('ESG', this.filteredProducts);
                this.addIfNotExists(
                    'Business Expansion',
                    this.filteredProducts
                );
            } else {
                this.removeProviders('Advisory Workshops');
                this.removeProviders('Business Plan');
                this.removeProviders('Financial Literacy');
                this.removeProviders('Marketing and Sales');
                this.removeProviders('Halal');
                this.removeProviders('ESG');
                this.removeProviders('Business Expansion');
            }

            this.events = this.initialProductsList;
            // Apply all filters at once
            this.filteredProducts.forEach((filter) => {
                this.filterProductFn(selectedProdCat, filter);
            });
        } else if (prodCat === 'Access New Markets') {
            this.events = this.initialProductsList;

            if (event.target.checked) {
                this.addIfNotExists(
                    'Access New Markets',
                    this.filteredProducts
                );
            } else {
                this.removeProviders('Access New Markets');
            }
            // Apply all filters at once
            this.filteredProducts.forEach((filter) => {
                this.filterProductFn(selectedProdCat, filter);
            });
        } else if (prodCat === 'Networking Sessions') {
            this.events = this.initialProductsList;
            if (event.target.checked) {
                this.addIfNotExists(
                    'Networking Sessions',
                    this.filteredProducts
                );
            } else {
                this.removeProviders('Networking Sessions');
            }
            // Apply all filters at once
            this.filteredProducts.forEach((filter) => {
                this.filterProductFn(selectedProdCat, filter);
            });
        } else {
            let subCatArr = [
                {
                    label: 'Business Plan',
                },
                {
                    label: 'Financial Literacy',
                },
                {
                    label: 'Marketing and Sales',
                },
                {
                    label: 'Halal',
                },
                {
                    label: 'ESG',
                },
                {
                    label: 'Business Expansion',
                },
            ];
            if (event.target.checked) {
                subCatArr.forEach((ele) => {
                    if (ele.label == prodCat) {
                        ele['checked'] = event.target.checked;
                        this.addIfNotExists(prodCat, this.filteredProducts);
                    }
                });

                let subCategoriesCheck = subCatArr.every(
                    (ele) => ele['checked'] == true
                );
                if (subCategoriesCheck) {
                    this.checkboxStates['Advisory Workshops'] =
                        event.target.checked;
                    this.addIfNotExists(
                        'Advisory Workshops',
                        this.filteredProducts
                    );
                }
            } else {
                subCatArr.forEach((ele) => {
                    if (ele.label == prodCat) {
                        ele['checked'] = event.target.checked;
                        this.removeProviders(prodCat);
                    }
                });
                this.checkboxStates['Advisory Workshops'] = false;
                this.removeProviders('Advisory Workshops');

                let removeIndex = this.filteredProducts.findIndex(
                    (item) => item === prodCat
                );
                if (removeIndex !== -1) {
                    this.filteredProducts.splice(removeIndex, 1);
                }
            }

            this.events = this.initialProductsList;
            // Apply all filters at once
            this.filteredProducts.forEach((filter) => {
                this.filterProductFn('Advisory Workshops', filter);
            });
        }
    }

    filterProductFn(selectedProdCat, filter?) {
        if (
            selectedProdCat == 'Advisory Workshops' ||
            selectedProdCat == 'Access New Markets' ||
            selectedProdCat == 'Networking Sessions'
        ) {
            let filteredEvents = this.events.filter((item: any) => {
                if (
                    this.filteredProducts.includes(item?.category) ||
                    this.filteredProducts.includes(item?.subcategory)
                ) {
                    return item;
                }
            });
            this.events = filteredEvents;
        }
    }

    nestedCopy(array) {
        return Array.from(array);
    }

    scrollToEvent(eventId) {
        this.applicationService.sendCalendarEventId.next(eventId);
    }

    readLocalStorageValue(key: string) {
        let value = localStorage.getItem(key);
        return value === 'en' ? value : 'ms';
    }
}
